import {
  TColor,
  TColorgateConfigurationOptions,
  TColorgateConfigurationResponse,
  TColorgateConfigurationValues,
  TDefaults,
  TObject,
  TOption,
  TTableData
} from './types';
import { setInitialFormValues } from '../../util/form';
import {
  BLANK_FIELDS,
  CENTIMETER_SIGN_FIELDS,
  COLOR_BLACK,
  COLORGATE_CONFIGURATION_TITLES,
  ColorgateConfigurationFields,
  ConfigurationChanges,
  PERCENTAGE_SIGN_FIELDS,
  RadioValues,
  SpecialValues,
  Units,
  WHITE_EDGES_OPTIONS,
  WHITE_FIELDS,
  WHITE_OPTIONS,
  WhiteEdges,
  WhiteOptionValues
} from './constants';

export const getInitialColorgateConfiguration = ({
  artworkConfiguration,
  blankConfiguration,
  color
}: TObject & TColor): TColorgateConfigurationValues => ({
  [ColorgateConfigurationFields.MediaName]: setInitialFormValues(blankConfiguration.mediaName),
  [ColorgateConfigurationFields.InkName]: setInitialFormValues(blankConfiguration.inkName),
  [ColorgateConfigurationFields.MetamodeName]: setInitialFormValues(blankConfiguration.metamodeName),
  [ColorgateConfigurationFields.SpecialColorsWhite]:
    getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.Mode) || WhiteOptionValues.Off,
  [ColorgateConfigurationFields.WhiteMinimum]: setWhiteMinimum(blankConfiguration.specialColors.white, color),
  [ColorgateConfigurationFields.WhiteLimit]:
    getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.Limit) !== null &&
    getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.Limit) !== undefined
      ? Number(getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.Limit))
      : 70,
  [ColorgateConfigurationFields.WhiteIntensity]:
    getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.Intensity) !== null &&
    getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.Intensity) !== undefined
      ? Number(getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.Intensity))
      : 65,
  [ColorgateConfigurationFields.WhiteSpreadChoke]:
    getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.SpreadChoke) !== null &&
    getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.SpreadChoke) !== undefined
      ? Number(getSpecialValueByKey(blankConfiguration.specialColors.white, SpecialValues.SpreadChoke))
      : -2,
  [ColorgateConfigurationFields.WhiteEdges]: setWhiteEdges(blankConfiguration.specialColors.white),
  [ColorgateConfigurationFields.SpecialColorsWhite2]: WhiteOptionValues.Off,
  [ColorgateConfigurationFields.PrintSetup]: setInitialFormValues(blankConfiguration.printSetup),
  [ColorgateConfigurationFields.PalletSize]: setInitialFormValues(blankConfiguration.palletSize),
  [ColorgateConfigurationFields.SprayAmount]: setInitialFormValues(blankConfiguration.sprayAmount),
  [ColorgateConfigurationFields.PrintHeight]: setInitialCentimeterValues(blankConfiguration.printHeight),
  [ColorgateConfigurationFields.MediaThickness]: setInitialFormValues(blankConfiguration.mediaThickness),
  [ColorgateConfigurationFields.DefaultSize]: setInitialRadioStringValues(artworkConfiguration.defaultSize),
  [ColorgateConfigurationFields.ProportionalSize]: setInitialRadioStringValues(artworkConfiguration.proportionalSize),
  [ColorgateConfigurationFields.ProportionalHeight]: setInitialCentimeterValues(
    artworkConfiguration.proportionalHeight
  ),
  [ColorgateConfigurationFields.ProportionalWidth]: setInitialCentimeterValues(artworkConfiguration.proportionalWidth),
  [ColorgateConfigurationFields.Rotation]: setInitialFormValues(artworkConfiguration.rotation),
  [ColorgateConfigurationFields.OffsetLeft]: setInitialCentimeterValues(artworkConfiguration.offsetLeft),
  [ColorgateConfigurationFields.OffsetTop]: setInitialCentimeterValues(artworkConfiguration.offsetTop),
  [ColorgateConfigurationFields.ColorCorrection]: setInitialFormValues(artworkConfiguration.colorCorrection),
  [ColorgateConfigurationFields.SaturationEnhancement]: setInitialFormValues(artworkConfiguration.saturationEnhancement)
});

export const setInitialRadioStringValues = (value: boolean): RadioValues => (value ? RadioValues.Yes : RadioValues.No);

const setInitialCentimeterValues = (value: string): number => {
  const centimeterValue = typeof +value === 'number' && +value / 28.346;

  return centimeterValue || centimeterValue === 0 ? centimeterValue : null;
};

export const getRadioBooleanValues = (value: RadioValues): boolean => value === RadioValues.Yes;

export const getColorgateConfigurationOptions = ({
  defaults,
  object
}: TColorgateConfigurationResponse): TColorgateConfigurationOptions => ({
  [ColorgateConfigurationFields.MediaName]: getOptions(
    'jdfConfigurationMediaName',
    defaults,
    object.blankConfiguration.mediaName
  ),
  [ColorgateConfigurationFields.InkName]: getOptions(
    'jdfConfigurationInkName',
    defaults,
    object.blankConfiguration.inkName
  ),
  [ColorgateConfigurationFields.MetamodeName]: getOptions(
    'jdfConfigurationMetamodeName',
    defaults,
    object.blankConfiguration.metamodeName
  ),
  [ColorgateConfigurationFields.SpecialColorsWhite]: WHITE_OPTIONS,
  [ColorgateConfigurationFields.WhiteEdges]: WHITE_EDGES_OPTIONS,
  [ColorgateConfigurationFields.SpecialColorsWhite2]: WHITE_OPTIONS,
  [ColorgateConfigurationFields.PrintSetup]: getOptions(
    'jdfConfigurationPrinterSetup',
    defaults,
    object.blankConfiguration.printSetup
  ),
  [ColorgateConfigurationFields.PalletSize]: getOptions(
    'jdfConfigurationPalletSize',
    defaults,
    object.blankConfiguration.palletSize
  ),
  [ColorgateConfigurationFields.SaturationEnhancement]: getOptions(
    'jdfConfigurationSaturationEnhancement',
    defaults,
    object.artworkConfiguration.saturationEnhancement
  ),
  [ColorgateConfigurationFields.Rotation]: getOptions(
    'jdfConfigurationRotation',
    defaults,
    object.artworkConfiguration.rotation
  )
});

export const getOptions = (key: string, defaults: TDefaults[], selectedValue: string): TOption[] => {
  if (!Array.isArray(defaults)) {
    return [];
  }
  const values = defaults.find((el) => el.key === key)?.values;
  const optionValues = Array.isArray(values) ? values.map((value) => ({ name: value, value: value })) : [];
  if (selectedValue && !values.includes(selectedValue)) {
    return [...optionValues, { name: selectedValue, value: selectedValue }];
  }
  return optionValues;
};

export const getBlankConfiguration = (values: TColorgateConfigurationValues): Record<string, any> => ({
  [ColorgateConfigurationFields.MediaName]: getFieldValue(values.mediaName),
  [ColorgateConfigurationFields.InkName]: getFieldValue(values.inkName),
  [ColorgateConfigurationFields.MetamodeName]: getFieldValue(values.metamodeName),
  specialColors: {
    white: getWhiteFieldValue(values),
    white2: getFieldValue(values.specialColorsWhite2)
  },
  [ColorgateConfigurationFields.PrintSetup]: getFieldValue(values.printSetup),
  [ColorgateConfigurationFields.PalletSize]: getFieldValue(values.palletSize),
  [ColorgateConfigurationFields.SprayAmount]: getFieldValue(values.sprayAmount),
  [ColorgateConfigurationFields.PrintHeight]: getPointValues(values.printHeight),
  [ColorgateConfigurationFields.MediaThickness]: getFieldValue(values.mediaThickness)
});

export const getArtworkConfiguration = (values: TColorgateConfigurationValues): Record<string, any> => ({
  [ColorgateConfigurationFields.DefaultSize]: getRadioBooleanValues(values.defaultSize),
  [ColorgateConfigurationFields.ProportionalSize]: getRadioBooleanValues(values.proportionalSize),
  [ColorgateConfigurationFields.ProportionalHeight]: getPointValues(values.proportionalHeight),
  [ColorgateConfigurationFields.ProportionalWidth]: getPointValues(values.proportionalWidth),
  [ColorgateConfigurationFields.Rotation]: getFieldValue(values.rotation),
  [ColorgateConfigurationFields.OffsetLeft]: getPointValues(values.offsetLeft),
  [ColorgateConfigurationFields.OffsetTop]: getPointValues(values.offsetTop),
  [ColorgateConfigurationFields.ColorCorrection]: getFieldValue(values.colorCorrection),
  [ColorgateConfigurationFields.SaturationEnhancement]: getFieldValue(values.saturationEnhancement)
});

export const getFieldValue = (value: string | number): string | number => (value === undefined ? '' : value);
const getWhiteFieldValue = (values: TColorgateConfigurationValues): string => {
  if (values.specialColorsWhite === WHITE_OPTIONS[0].value) {
    return `Mode=${values.specialColorsWhite}`;
  }
  return `Mode=${values.specialColorsWhite},Minimum=${values.whiteMinimum},Limit=${values.whiteLimit},Intensity=${values.whiteIntensity},SpreadChoke=${values.whiteSpreadChoke},Edges=${values.whiteEdges}`;
};
const getPointValues = (value: number | null): string => {
  const pointValue = typeof value === 'number' && value * 28.346;
  return pointValue || pointValue === 0 ? pointValue.toString() : '';
};

export const getModifiedValues = (
  type: ConfigurationChanges,
  values: TColorgateConfigurationValues,
  initialValues: TColorgateConfigurationValues
): TTableData[] => {
  return Object.entries(values)
    .map(([key, value]) => {
      if (type === ConfigurationChanges.Blank && BLANK_FIELDS.includes(key) && value !== initialValues[key]) {
        if (WHITE_FIELDS.includes(key) && values.specialColorsWhite === WhiteOptionValues.Off) {
          return null;
        }
        const unit = getFieldUnit(key);
        return { field: COLORGATE_CONFIGURATION_TITLES[key], value: `${value}${unit}` };
      }
      if (type === ConfigurationChanges.Artwork && !BLANK_FIELDS.includes(key) && value !== initialValues[key]) {
        const unit = getFieldUnit(key);
        return { field: COLORGATE_CONFIGURATION_TITLES[key], value: `${value}${unit}` };
      }
      return null;
    })
    .filter((el) => el !== null);
};

const getFieldUnit = (key: string): Units | '' => {
  const isPercentage = PERCENTAGE_SIGN_FIELDS.includes(key);
  const isCentimeter = CENTIMETER_SIGN_FIELDS.includes(key);

  switch (true) {
    case isPercentage:
      return Units.Percentage;

    case isCentimeter:
      return Units.Centimeter;

    default:
      return '';
  }
};

const getSpecialValueByKey = (stringValue: string, key: SpecialValues): string => {
  if (!stringValue) {
    return null;
  }
  const [value] = stringValue
    .split(',')
    .map((optionValue) => {
      const pair = optionValue.split('=');
      return pair[0] === key ? pair[1] : null;
    })
    .flat()
    .filter((propValue) => propValue !== null);

  return value;
};

const setWhiteMinimum = (whiteOptionValue: string, color: string): number => {
  const minimum = getSpecialValueByKey(whiteOptionValue, SpecialValues.Minimum);

  if (minimum) {
    return Number(minimum);
  }
  return color.toLowerCase() === COLOR_BLACK ? 15 : 40;
};

const setWhiteEdges = (whiteOptionValue: string): string => {
  const edges = getSpecialValueByKey(whiteOptionValue, SpecialValues.Edges);

  if (edges !== WhiteEdges.Zero && edges !== WhiteEdges.One && edges !== WhiteEdges.Two) {
    return WhiteEdges.Zero;
  }
  return edges;
};
